// BUILD 2 C2-13: complete per-locale confirmation and card UI copy.
import type { NamespaceCatalog } from '@/i18n/ns';

const keys = [
  'approve',
  'reject',
  'approvingIn',
  'rejectingIn',
  'cancel',
  'holdToSayYes',
  'heardYou',
  'notSure',
  'confirmForget',
  'forgotten',
  'stagedExpired',
  'approvalNeedsAdmin',
  'tapToApproveConnector',
  'executed',
  'failed',
  'unknownCheckService',
  'withdrawn',
  'errorStale',
  'errorSpokenApprovalsOff',
  'errorApproverPolicyMissing',
  'errorNotPending',
  'errorPaymentRequired',
  'reviewBeforeRuns',
  'confirmation',
  'needsApproval',
  'running',
  'completed',
  'rejectedStatus',
  'failedStatus',
  'statusUnavailable',
  'actionLabel',
  'connectionLabel',
  'argumentsLabel',
  'tapOnly',
  'approveAndRun',
  'working',
  'connectedServiceAction',
  'connectedService',
] as const;
export type VoiceConfirmCopyKey = (typeof keys)[number];

const en = {
  approve: 'Approve',
  reject: 'Reject',
  approvingIn: 'Approving in {seconds} s',
  rejectingIn: 'Rejecting in {seconds} s',
  cancel: 'Cancel',
  holdToSayYes: 'Hold to answer yes or no',
  heardYou: 'Heard: “{text}”',
  notSure: 'That was not a clear yes or no. Tap to answer.',
  confirmForget: 'Forget this memory?',
  forgotten: 'Forgotten',
  stagedExpired: 'This request expired. Ask again if you still want it.',
  approvalNeedsAdmin: 'An admin needs to approve this.',
  tapToApproveConnector: 'Actions in connected services are approved with a tap.',
  executed: 'Done',
  failed: 'This did not go through.',
  unknownCheckService: 'The result could not be confirmed. Check the service before trying again.',
  withdrawn: 'Withdrawn',
  errorStale: 'This card changed. Review it again.',
  errorSpokenApprovalsOff: 'Spoken confirmations are off. Tap to confirm.',
  errorApproverPolicyMissing: 'Confirm actions in connected services on screen.',
  errorNotPending: 'This was already decided.',
  errorPaymentRequired:
    'Your company’s usage credit is used up. An administrator can add a card, then try again.',
  reviewBeforeRuns: 'Review before it runs',
  confirmation: 'Confirmation',
  needsApproval: 'Needs your approval',
  running: 'Running',
  completed: 'Completed',
  rejectedStatus: 'Rejected',
  failedStatus: 'Failed',
  statusUnavailable: 'Status unavailable',
  actionLabel: 'Action',
  connectionLabel: 'Connection',
  argumentsLabel: 'Arguments',
  tapOnly: 'Actions in connected services are approved with a tap.',
  approveAndRun: 'Approve and run',
  working: 'Working…',
  connectedServiceAction: 'Connected service action',
  connectedService: 'Connected service',
};

const bg = {
  approve: 'Одобри',
  reject: 'Откажи',
  approvingIn: 'Одобри · {seconds} s',
  rejectingIn: 'Откажи · {seconds} s',
  cancel: 'Отмени',
  holdToSayYes: 'Задръжте, за да отговорите с да или не',
  heardYou: 'Чух: „{text}“',
  notSure: 'Това не беше ясно да или не. Докоснете, за да отговорите.',
  confirmForget: 'Забрави тази памет?',
  forgotten: 'Забравено',
  stagedExpired: 'Тази заявка изтече. Поискайте отново, ако все още я искате.',
  approvalNeedsAdmin: 'Администратор трябва да одобри това.',
  tapToApproveConnector: 'Действията в свързани услуги се одобряват с докосване.',
  executed: 'Готово',
  failed: 'Това не премина.',
  unknownCheckService: 'Резултатът не може да бъде потвърден. Проверете услугата.',
  withdrawn: 'Оттеглено',
  errorStale: 'Картата се промени. Прегледайте я отново.',
  errorSpokenApprovalsOff: 'Гласовите потвърждения са изключени. Докоснете за потвърждение.',
  errorApproverPolicyMissing: 'Потвърдете действията в свързаните услуги на екрана.',
  errorNotPending: 'Това вече е решено.',
  errorPaymentRequired: 'Кредитът на компанията е изчерпан. Администратор може да добави карта.',
  reviewBeforeRuns: 'Преглед преди изпълнение',
  confirmation: 'Потвърждение',
  needsApproval: 'Изисква вашето одобрение',
  running: 'Изпълнява се',
  completed: 'Завършено',
  rejectedStatus: 'Отхвърлено',
  failedStatus: 'Неуспешно',
  statusUnavailable: 'Състоянието не е налично',
  actionLabel: 'Действие',
  connectionLabel: 'Връзка',
  argumentsLabel: 'Аргументи',
  tapOnly: 'Действията в свързани услуги се одобряват с докосване.',
  approveAndRun: 'Одобри и изпълни',
  working: 'Работи…',
  connectedServiceAction: 'Действие в свързана услуга',
  connectedService: 'Свързана услуга',
};

const hr = {
  approve: 'Odobri',
  reject: 'Odbij',
  approvingIn: 'Odobri · {seconds} s',
  rejectingIn: 'Odbij · {seconds} s',
  cancel: 'Odustani',
  holdToSayYes: 'Držite za odgovor da ili ne',
  heardYou: 'Čujem: „{text}“',
  notSure: 'To nije bio jasan odgovor da ili ne. Dodirnite za odgovor.',
  confirmForget: 'Zaboraviti ovu memoriju?',
  forgotten: 'Zaboravljeno',
  stagedExpired: 'Ovaj je zahtjev istekao. Zatražite ga ponovno ako ga još želite.',
  approvalNeedsAdmin: 'Administrator mora ovo odobriti.',
  tapToApproveConnector: 'Radnje u povezanim uslugama odobravaju se dodirom.',
  executed: 'Gotovo',
  failed: 'Ovo nije prošlo.',
  unknownCheckService: 'Rezultat se ne može potvrditi. Provjerite uslugu.',
  withdrawn: 'Povučeno',
  errorStale: 'Kartica se promijenila. Ponovno je pregledajte.',
  errorSpokenApprovalsOff: 'Glasovne potvrde su isključene. Dodirnite za potvrdu.',
  errorApproverPolicyMissing: 'Potvrdite radnje u povezanim uslugama na zaslonu.',
  errorNotPending: 'O ovome je već odlučeno.',
  errorPaymentRequired: 'Kredit tvrtke je potrošen. Administrator može dodati karticu.',
  reviewBeforeRuns: 'Pregledajte prije pokretanja',
  confirmation: 'Potvrda',
  needsApproval: 'Potrebno je vaše odobrenje',
  running: 'U tijeku',
  completed: 'Dovršeno',
  rejectedStatus: 'Odbijeno',
  failedStatus: 'Neuspješno',
  statusUnavailable: 'Status nije dostupan',
  actionLabel: 'Radnja',
  connectionLabel: 'Veza',
  argumentsLabel: 'Argumenti',
  tapOnly: 'Radnje u povezanim uslugama odobravaju se dodirom.',
  approveAndRun: 'Odobri i pokreni',
  working: 'Radi…',
  connectedServiceAction: 'Radnja povezane usluge',
  connectedService: 'Povezana usluga',
};

const cs = {
  approve: 'Schválit',
  reject: 'Zamítnout',
  approvingIn: 'Schválit · {seconds} s',
  rejectingIn: 'Zamítnout · {seconds} s',
  cancel: 'Zrušit',
  holdToSayYes: 'Podržte pro odpověď ano nebo ne',
  heardYou: 'Slyšel jsem: „{text}“',
  notSure: 'To nebylo jasné ano ani ne. Klepnutím odpovězte.',
  confirmForget: 'Zapomenout tuto vzpomínku?',
  forgotten: 'Zapomenuto',
  stagedExpired: 'Tato žádost vypršela. Pokud ji stále chcete, požádejte znovu.',
  approvalNeedsAdmin: 'Toto musí schválit správce.',
  tapToApproveConnector: 'Akce v připojených službách se schvalují klepnutím.',
  executed: 'Hotovo',
  failed: 'Toto se nezdařilo.',
  unknownCheckService: 'Výsledek nelze potvrdit. Zkontrolujte službu.',
  withdrawn: 'Staženo',
  errorStale: 'Karta se změnila. Zkontrolujte ji znovu.',
  errorSpokenApprovalsOff: 'Hlasová potvrzení jsou vypnutá. Klepnutím potvrďte.',
  errorApproverPolicyMissing: 'Potvrďte akce v připojených službách na obrazovce.',
  errorNotPending: 'O tomto již bylo rozhodnuto.',
  errorPaymentRequired: 'Kredit společnosti je vyčerpán. Správce může přidat kartu.',
  reviewBeforeRuns: 'Zkontrolujte před spuštěním',
  confirmation: 'Potvrzení',
  needsApproval: 'Vyžaduje vaše schválení',
  running: 'Spouští se',
  completed: 'Dokončeno',
  rejectedStatus: 'Zamítnuto',
  failedStatus: 'Selhalo',
  statusUnavailable: 'Stav není dostupný',
  actionLabel: 'Akce',
  connectionLabel: 'Připojení',
  argumentsLabel: 'Argumenty',
  tapOnly: 'Akce v připojených službách se schvalují klepnutím.',
  approveAndRun: 'Schválit a spustit',
  working: 'Pracuji…',
  connectedServiceAction: 'Akce připojené služby',
  connectedService: 'Připojená služba',
};

const da = {
  approve: 'Godkend',
  reject: 'Afvis',
  approvingIn: 'Godkend · {seconds} s',
  rejectingIn: 'Afvis · {seconds} s',
  cancel: 'Annuller',
  holdToSayYes: 'Hold for at svare ja eller nej',
  heardYou: 'Hørte: “{text}”',
  notSure: 'Det var ikke et klart ja eller nej. Tryk for at svare.',
  confirmForget: 'Glem denne hukommelse?',
  forgotten: 'Glemt',
  stagedExpired: 'Denne anmodning er udløbet. Spørg igen, hvis du stadig ønsker den.',
  approvalNeedsAdmin: 'En administrator skal godkende dette.',
  tapToApproveConnector: 'Handlinger i tilsluttede tjenester godkendes med et tryk.',
  executed: 'Færdig',
  failed: 'Dette gik ikke igennem.',
  unknownCheckService: 'Resultatet kunne ikke bekræftes. Kontrollér tjenesten.',
  withdrawn: 'Trukket tilbage',
  errorStale: 'Kortet blev ændret. Gennemgå det igen.',
  errorSpokenApprovalsOff: 'Talebekræftelser er slået fra. Tryk for at bekræfte.',
  errorApproverPolicyMissing: 'Bekræft handlinger i tilsluttede tjenester på skærmen.',
  errorNotPending: 'Dette er allerede afgjort.',
  errorPaymentRequired: 'Virksomhedens kredit er opbrugt. En administrator kan tilføje et kort.',
  reviewBeforeRuns: 'Gennemgå før kørsel',
  confirmation: 'Bekræftelse',
  needsApproval: 'Kræver din godkendelse',
  running: 'Kører',
  completed: 'Gennemført',
  rejectedStatus: 'Afvist',
  failedStatus: 'Mislykkedes',
  statusUnavailable: 'Status er ikke tilgængelig',
  actionLabel: 'Handling',
  connectionLabel: 'Forbindelse',
  argumentsLabel: 'Argumenter',
  tapOnly: 'Handlinger i tilsluttede tjenester godkendes med et tryk.',
  approveAndRun: 'Godkend og kør',
  working: 'Arbejder…',
  connectedServiceAction: 'Handling i tilsluttet tjeneste',
  connectedService: 'Tilsluttet tjeneste',
};

const nl = {
  approve: 'Goedkeuren',
  reject: 'Afwijzen',
  approvingIn: 'Goedkeuren · {seconds} s',
  rejectingIn: 'Afwijzen · {seconds} s',
  cancel: 'Annuleren',
  holdToSayYes: 'Houd vast om ja of nee te antwoorden',
  heardYou: 'Gehoord: “{text}”',
  notSure: 'Dat was geen duidelijk ja of nee. Tik om te antwoorden.',
  confirmForget: 'Dit geheugen vergeten?',
  forgotten: 'Vergeten',
  stagedExpired: 'Dit verzoek is verlopen. Vraag het opnieuw als je het nog wilt.',
  approvalNeedsAdmin: 'Een beheerder moet dit goedkeuren.',
  tapToApproveConnector: 'Acties in verbonden diensten worden met een tik goedgekeurd.',
  executed: 'Klaar',
  failed: 'Dit is niet gelukt.',
  unknownCheckService: 'Het resultaat kon niet worden bevestigd. Controleer de dienst.',
  withdrawn: 'Ingetrokken',
  errorStale: 'Deze kaart is gewijzigd. Controleer haar opnieuw.',
  errorSpokenApprovalsOff: 'Gesproken bevestigingen zijn uitgeschakeld. Tik om te bevestigen.',
  errorApproverPolicyMissing: 'Bevestig acties in verbonden diensten op het scherm.',
  errorNotPending: 'Hierover is al beslist.',
  errorPaymentRequired:
    'Het gebruikstegoed van je bedrijf is op. Een beheerder kan een kaart toevoegen.',
  reviewBeforeRuns: 'Controleren voordat het wordt uitgevoerd',
  confirmation: 'Bevestiging',
  needsApproval: 'Jouw goedkeuring vereist',
  running: 'Bezig',
  completed: 'Voltooid',
  rejectedStatus: 'Afgewezen',
  failedStatus: 'Mislukt',
  statusUnavailable: 'Status niet beschikbaar',
  actionLabel: 'Actie',
  connectionLabel: 'Verbinding',
  argumentsLabel: 'Argumenten',
  tapOnly: 'Acties in verbonden diensten worden met een tik goedgekeurd.',
  approveAndRun: 'Goedkeuren en uitvoeren',
  working: 'Bezig…',
  connectedServiceAction: 'Actie van verbonden dienst',
  connectedService: 'Verbonden dienst',
};

const fr = {
  approve: 'Approuver',
  reject: 'Refuser',
  approvingIn: 'Approuver · {seconds} s',
  rejectingIn: 'Refuser · {seconds} s',
  cancel: 'Annuler',
  holdToSayYes: 'Maintenez pour répondre oui ou non',
  heardYou: 'Entendu : « {text} »',
  notSure: 'Ce n’était pas un oui ou un non clair. Touchez pour répondre.',
  confirmForget: 'Oublier cette mémoire ?',
  forgotten: 'Oublié',
  stagedExpired: 'Cette demande a expiré. Demandez-la de nouveau si vous la voulez toujours.',
  approvalNeedsAdmin: 'Un administrateur doit approuver ceci.',
  tapToApproveConnector: 'Les actions des services connectés sont approuvées par une pression.',
  executed: 'Terminé',
  failed: 'Cela n’a pas abouti.',
  unknownCheckService: 'Le résultat n’a pas pu être confirmé. Vérifiez le service.',
  withdrawn: 'Retiré',
  errorStale: 'Cette carte a changé. Vérifiez-la de nouveau.',
  errorSpokenApprovalsOff: 'Les confirmations vocales sont désactivées. Touchez pour confirmer.',
  errorApproverPolicyMissing: 'Confirmez les actions des services connectés à l’écran.',
  errorNotPending: 'Cette décision a déjà été prise.',
  errorPaymentRequired:
    'Le crédit d’utilisation de votre entreprise est épuisé. Un administrateur peut ajouter une carte.',
  reviewBeforeRuns: 'Vérifier avant exécution',
  confirmation: 'Confirmation',
  needsApproval: 'Votre approbation est requise',
  running: 'En cours',
  completed: 'Terminé',
  rejectedStatus: 'Refusé',
  failedStatus: 'Échec',
  statusUnavailable: 'État indisponible',
  actionLabel: 'Action',
  connectionLabel: 'Connexion',
  argumentsLabel: 'Arguments',
  tapOnly: 'Les actions des services connectés sont approuvées par une pression.',
  approveAndRun: 'Approuver et exécuter',
  working: 'En cours…',
  connectedServiceAction: 'Action du service connecté',
  connectedService: 'Service connecté',
};

const de = {
  approve: 'Genehmigen',
  reject: 'Ablehnen',
  approvingIn: 'Genehmigen · {seconds} s',
  rejectingIn: 'Ablehnen · {seconds} s',
  cancel: 'Abbrechen',
  holdToSayYes: 'Halten Sie gedrückt, um Ja oder Nein zu antworten',
  heardYou: 'Gehört: „{text}“',
  notSure: 'Das war kein klares Ja oder Nein. Tippen Sie zum Antworten.',
  confirmForget: 'Diese Erinnerung vergessen?',
  forgotten: 'Vergessen',
  stagedExpired: 'Diese Anfrage ist abgelaufen. Fragen Sie erneut, wenn Sie sie noch möchten.',
  approvalNeedsAdmin: 'Ein Administrator muss dies genehmigen.',
  tapToApproveConnector: 'Aktionen in verbundenen Diensten werden per Tippen genehmigt.',
  executed: 'Fertig',
  failed: 'Dies wurde nicht ausgeführt.',
  unknownCheckService: 'Das Ergebnis konnte nicht bestätigt werden. Prüfen Sie den Dienst.',
  withdrawn: 'Zurückgezogen',
  errorStale: 'Diese Karte wurde geändert. Prüfen Sie sie erneut.',
  errorSpokenApprovalsOff: 'Gesprochene Bestätigungen sind deaktiviert. Zum Bestätigen tippen.',
  errorApproverPolicyMissing: 'Bestätigen Sie Aktionen verbundener Dienste auf dem Bildschirm.',
  errorNotPending: 'Dies wurde bereits entschieden.',
  errorPaymentRequired:
    'Das Nutzungsguthaben Ihres Unternehmens ist aufgebraucht. Ein Administrator kann eine Karte hinzufügen.',
  reviewBeforeRuns: 'Vor der Ausführung prüfen',
  confirmation: 'Bestätigung',
  needsApproval: 'Ihre Genehmigung ist erforderlich',
  running: 'Wird ausgeführt',
  completed: 'Abgeschlossen',
  rejectedStatus: 'Abgelehnt',
  failedStatus: 'Fehlgeschlagen',
  statusUnavailable: 'Status nicht verfügbar',
  actionLabel: 'Aktion',
  connectionLabel: 'Verbindung',
  argumentsLabel: 'Argumente',
  tapOnly: 'Aktionen in verbundenen Diensten werden per Tippen genehmigt.',
  approveAndRun: 'Genehmigen und ausführen',
  working: 'Wird bearbeitet…',
  connectedServiceAction: 'Aktion eines verbundenen Dienstes',
  connectedService: 'Verbundener Dienst',
};

const es = {
  approve: 'Aprobar',
  reject: 'Rechazar',
  approvingIn: 'Aprobar · {seconds} s',
  rejectingIn: 'Rechazar · {seconds} s',
  cancel: 'Cancelar',
  holdToSayYes: 'Mantén pulsado para responder sí o no',
  heardYou: 'He oído: «{text}»',
  notSure: 'No ha sido un sí o un no claro. Toca para responder.',
  confirmForget: '¿Olvidar esta memoria?',
  forgotten: 'Olvidado',
  stagedExpired: 'Esta solicitud ha caducado. Pídela de nuevo si aún la quieres.',
  approvalNeedsAdmin: 'Un administrador debe aprobar esto.',
  tapToApproveConnector: 'Las acciones de servicios conectados se aprueban con un toque.',
  executed: 'Hecho',
  failed: 'Esto no se ha completado.',
  unknownCheckService: 'No se pudo confirmar el resultado. Comprueba el servicio.',
  withdrawn: 'Retirado',
  errorStale: 'Esta tarjeta ha cambiado. Revísala de nuevo.',
  errorSpokenApprovalsOff: 'Las confirmaciones habladas están desactivadas. Toca para confirmar.',
  errorApproverPolicyMissing: 'Confirma en pantalla las acciones de los servicios conectados.',
  errorNotPending: 'Esto ya se había decidido.',
  errorPaymentRequired:
    'El crédito de uso de tu empresa se ha agotado. Un administrador puede añadir una tarjeta.',
  reviewBeforeRuns: 'Revisar antes de ejecutar',
  confirmation: 'Confirmación',
  needsApproval: 'Necesita tu aprobación',
  running: 'En curso',
  completed: 'Completado',
  rejectedStatus: 'Rechazado',
  failedStatus: 'Fallido',
  statusUnavailable: 'Estado no disponible',
  actionLabel: 'Acción',
  connectionLabel: 'Conexión',
  argumentsLabel: 'Argumentos',
  tapOnly: 'Las acciones de servicios conectados se aprueban con un toque.',
  approveAndRun: 'Aprobar y ejecutar',
  working: 'Trabajando…',
  connectedServiceAction: 'Acción del servicio conectado',
  connectedService: 'Servicio conectado',
};

const sv = {
  approve: 'Godkänn',
  reject: 'Avvisa',
  approvingIn: 'Godkänn · {seconds} s',
  rejectingIn: 'Avvisa · {seconds} s',
  cancel: 'Avbryt',
  holdToSayYes: 'Håll för att svara ja eller nej',
  heardYou: 'Hörde: ”{text}”',
  notSure: 'Det var inget tydligt ja eller nej. Tryck för att svara.',
  confirmForget: 'Glöm detta minne?',
  forgotten: 'Glömd',
  stagedExpired: 'Den här begäran har löpt ut. Fråga igen om du fortfarande vill ha den.',
  approvalNeedsAdmin: 'En administratör måste godkänna detta.',
  tapToApproveConnector: 'Åtgärder i anslutna tjänster godkänns med ett tryck.',
  executed: 'Klart',
  failed: 'Det gick inte igenom.',
  unknownCheckService: 'Resultatet kunde inte bekräftas. Kontrollera tjänsten.',
  withdrawn: 'Återkallad',
  errorStale: 'Kortet ändrades. Granska det igen.',
  errorSpokenApprovalsOff: 'Talade bekräftelser är avstängda. Tryck för att bekräfta.',
  errorApproverPolicyMissing: 'Bekräfta åtgärder i anslutna tjänster på skärmen.',
  errorNotPending: 'Detta är redan avgjort.',
  errorPaymentRequired:
    'Företagets användningskredit är slut. En administratör kan lägga till ett kort.',
  reviewBeforeRuns: 'Granska innan körning',
  confirmation: 'Bekräftelse',
  needsApproval: 'Ditt godkännande krävs',
  running: 'Körs',
  completed: 'Slutförd',
  rejectedStatus: 'Avvisad',
  failedStatus: 'Misslyckades',
  statusUnavailable: 'Status ej tillgänglig',
  actionLabel: 'Åtgärd',
  connectionLabel: 'Anslutning',
  argumentsLabel: 'Argument',
  tapOnly: 'Åtgärder i anslutna tjänster godkänns med ett tryck.',
  approveAndRun: 'Godkänn och kör',
  working: 'Arbetar…',
  connectedServiceAction: 'Åtgärd i ansluten tjänst',
  connectedService: 'Ansluten tjänst',
};

const et = {
  approve: 'Kinnita',
  reject: 'Keeldu',
  approvingIn: 'Kinnita · {seconds} s',
  rejectingIn: 'Keeldu · {seconds} s',
  cancel: 'Tühista',
  holdToSayYes: 'Vasta jah või ei, hoides all',
  heardYou: 'Kuulsin: „{text}“',
  notSure: 'See polnud selge jah ega ei. Vastamiseks puuduta.',
  confirmForget: 'Kas unustada see mälestus?',
  forgotten: 'Unustatud',
  stagedExpired: 'See taotlus aegus. Küsi uuesti, kui soovid seda endiselt.',
  approvalNeedsAdmin: 'Administraator peab selle kinnitama.',
  tapToApproveConnector: 'Ühendatud teenuste toimingud kinnitatakse puudutusega.',
  executed: 'Valmis',
  failed: 'See ei õnnestunud.',
  unknownCheckService: 'Tulemust ei saanud kinnitada. Kontrolli teenust.',
  withdrawn: 'Tagasi võetud',
  errorStale: 'See kaart muutus. Vaata see uuesti üle.',
  errorSpokenApprovalsOff: 'Häälega kinnitamine on väljas. Kinnitamiseks puuduta.',
  errorApproverPolicyMissing: 'Kinnita ühendatud teenuste toimingud ekraanil.',
  errorNotPending: 'See on juba otsustatud.',
  errorPaymentRequired: 'Ettevõtte kasutuskrediit on otsas. Administraator saab kaardi lisada.',
  reviewBeforeRuns: 'Vaata enne käivitamist üle',
  confirmation: 'Kinnitus',
  needsApproval: 'Vajab sinu kinnitust',
  running: 'Töötab',
  completed: 'Lõpetatud',
  rejectedStatus: 'Tagasi lükatud',
  failedStatus: 'Ebaõnnestus',
  statusUnavailable: 'Olek pole saadaval',
  actionLabel: 'Toiming',
  connectionLabel: 'Ühendus',
  argumentsLabel: 'Argumendid',
  tapOnly: 'Ühendatud teenuste toimingud kinnitatakse puudutusega.',
  approveAndRun: 'Kinnita ja käivita',
  working: 'Töötlus…',
  connectedServiceAction: 'Ühendatud teenuse toiming',
  connectedService: 'Ühendatud teenus',
};

const fi = {
  approve: 'Hyväksy',
  reject: 'Hylkää',
  approvingIn: 'Hyväksy · {seconds} s',
  rejectingIn: 'Hylkää · {seconds} s',
  cancel: 'Peruuta',
  holdToSayYes: 'Pidä painettuna ja vastaa kyllä tai ei',
  heardYou: 'Kuulin: ”{text}”',
  notSure: 'Se ei olnud selkeä kyllä tai ei. Vastaa napauttamalla.',
  confirmForget: 'Unohdetaanko tämä muisto?',
  forgotten: 'Unohdettu',
  stagedExpired: 'Pyyntö vanheni. Pyydä uudelleen, jos haluat sen edelleen.',
  approvalNeedsAdmin: 'Ylläpitäjän on hyväksyttävä tämä.',
  tapToApproveConnector: 'Yhdistettyjen palvelujen toiminnot hyväksytään napauttamalla.',
  executed: 'Valmis',
  failed: 'Tämä ei onnistunut.',
  unknownCheckService: 'Tulosta ei voitu vahvistaa. Tarkista palvelu.',
  withdrawn: 'Peruttu',
  errorStale: 'Kortti muuttui. Tarkista se uudelleen.',
  errorSpokenApprovalsOff: 'Puhevahvistukset ovat poissa käytöstä. Vahvista napauttamalla.',
  errorApproverPolicyMissing: 'Vahvista yhdistettyjen palvelujen toiminnot näytöllä.',
  errorNotPending: 'Tästä on jo päätetty.',
  errorPaymentRequired: 'Yrityksen käyttöluotto on käytetty. Ylläpitäjä voi lisätä kortin.',
  reviewBeforeRuns: 'Tarkista ennen suorittamista',
  confirmation: 'Vahvistus',
  needsApproval: 'Hyväksyntäsi vaaditaan',
  running: 'Suoritetaan',
  completed: 'Valmis',
  rejectedStatus: 'Hylätty',
  failedStatus: 'Epäonnistui',
  statusUnavailable: 'Tila ei ole saatavilla',
  actionLabel: 'Toiminto',
  connectionLabel: 'Yhteys',
  argumentsLabel: 'Argumentit',
  tapOnly: 'Yhdistettyjen palvelujen toiminnot hyväksytään napauttamalla.',
  approveAndRun: 'Hyväksy ja suorita',
  working: 'Käsitellään…',
  connectedServiceAction: 'Yhdistetyn palvelun toiminto',
  connectedService: 'Yhdistetty palvelu',
};

const it = {
  approve: 'Approva',
  reject: 'Rifiuta',
  approvingIn: 'Approva · {seconds} s',
  rejectingIn: 'Rifiuta · {seconds} s',
  cancel: 'Annulla',
  holdToSayYes: 'Tieni premuto per rispondere sì o no',
  heardYou: 'Ho sentito: «{text}»',
  notSure: 'Non era un sì o un no chiaro. Tocca per rispondere.',
  confirmForget: 'Dimenticare questa memoria?',
  forgotten: 'Dimenticato',
  stagedExpired: 'Questa richiesta è scaduta. Chiedi di nuovo se la vuoi ancora.',
  approvalNeedsAdmin: 'Un amministratore deve approvarlo.',
  tapToApproveConnector: 'Le azioni nei servizi collegati si approvano con un tocco.',
  executed: 'Fatto',
  failed: 'Non è andato a buon fine.',
  unknownCheckService: 'Non è stato possibile confermare il risultato. Controlla il servizio.',
  withdrawn: 'Ritirato',
  errorStale: 'Questa scheda è cambiata. Controllala di nuovo.',
  errorSpokenApprovalsOff: 'Le conferme vocali sono disattivate. Tocca per confermare.',
  errorApproverPolicyMissing: 'Conferma sullo schermo le azioni nei servizi collegati.',
  errorNotPending: 'È già stato deciso.',
  errorPaymentRequired:
    'Il credito d’uso aziendale è esaurito. Un amministratore può aggiungere una carta.',
  reviewBeforeRuns: 'Controlla prima di eseguire',
  confirmation: 'Conferma',
  needsApproval: 'Serve la tua approvazione',
  running: 'In esecuzione',
  completed: 'Completato',
  rejectedStatus: 'Rifiutato',
  failedStatus: 'Non riuscito',
  statusUnavailable: 'Stato non disponibile',
  actionLabel: 'Azione',
  connectionLabel: 'Connessione',
  argumentsLabel: 'Argomenti',
  tapOnly: 'Le azioni nei servizi collegati si approvano con un tocco.',
  approveAndRun: 'Approva ed esegui',
  working: 'In corso…',
  connectedServiceAction: 'Azione del servizio collegato',
  connectedService: 'Servizio collegato',
};

const pt = {
  approve: 'Aprovar',
  reject: 'Rejeitar',
  approvingIn: 'Aprovar · {seconds} s',
  rejectingIn: 'Rejeitar · {seconds} s',
  cancel: 'Cancelar',
  holdToSayYes: 'Mantenha premido para responder sim ou não',
  heardYou: 'Ouvi: “{text}”',
  notSure: 'Não foi um sim ou não claro. Toque para responder.',
  confirmForget: 'Esquecer esta memória?',
  forgotten: 'Esquecido',
  stagedExpired: 'Este pedido expirou. Peça novamente se ainda o quiser.',
  approvalNeedsAdmin: 'Um administrador tem de aprovar isto.',
  tapToApproveConnector: 'As ações em serviços ligados são aprovadas com um toque.',
  executed: 'Concluído',
  failed: 'Isto não avançou.',
  unknownCheckService: 'Não foi possível confirmar o resultado. Verifique o serviço.',
  withdrawn: 'Retirado',
  errorStale: 'Este cartão mudou. Reveja-o novamente.',
  errorSpokenApprovalsOff: 'As confirmações faladas estão desligadas. Toque para confirmar.',
  errorApproverPolicyMissing: 'Confirme no ecrã as ações dos serviços ligados.',
  errorNotPending: 'Isto já foi decidido.',
  errorPaymentRequired:
    'O crédito de utilização da empresa terminou. Um administrador pode adicionar um cartão.',
  reviewBeforeRuns: 'Rever antes de executar',
  confirmation: 'Confirmação',
  needsApproval: 'Requer a sua aprovação',
  running: 'A executar',
  completed: 'Concluído',
  rejectedStatus: 'Rejeitado',
  failedStatus: 'Falhou',
  statusUnavailable: 'Estado indisponível',
  actionLabel: 'Ação',
  connectionLabel: 'Ligação',
  argumentsLabel: 'Argumentos',
  tapOnly: 'As ações em serviços ligados são aprovadas com um toque.',
  approveAndRun: 'Aprovar e executar',
  working: 'A trabalhar…',
  connectedServiceAction: 'Ação do serviço ligado',
  connectedService: 'Serviço ligado',
};

const pl = {
  approve: 'Zatwierdź',
  reject: 'Odrzuć',
  approvingIn: 'Zatwierdź · {seconds} s',
  rejectingIn: 'Odrzuć · {seconds} s',
  cancel: 'Anuluj',
  holdToSayYes: 'Przytrzymaj, aby odpowiedzieć tak lub nie',
  heardYou: 'Usłyszałem: „{text}”',
  notSure: 'To nie było wyraźne tak ani nie. Dotknij, aby odpowiedzieć.',
  confirmForget: 'Zapomnieć tę pamięć?',
  forgotten: 'Zapomniano',
  stagedExpired: 'To żądanie wygasło. Poproś ponownie, jeśli nadal go chcesz.',
  approvalNeedsAdmin: 'Administrator musi to zatwierdzić.',
  tapToApproveConnector: 'Działania w połączonych usługach zatwierdza się dotknięciem.',
  executed: 'Gotowe',
  failed: 'To się nie powiodło.',
  unknownCheckService: 'Nie można potwierdzić wyniku. Sprawdź usługę.',
  withdrawn: 'Wycofano',
  errorStale: 'Ta karta się zmieniła. Sprawdź ją ponownie.',
  errorSpokenApprovalsOff: 'Potwierdzenia głosowe są wyłączone. Dotknij, aby potwierdzić.',
  errorApproverPolicyMissing: 'Potwierdź na ekranie działania w połączonych usługach.',
  errorNotPending: 'To już zostało rozstrzygnięte.',
  errorPaymentRequired: 'Kredyt użytkowy firmy został wyczerpany. Administrator może dodać kartę.',
  reviewBeforeRuns: 'Sprawdź przed uruchomieniem',
  confirmation: 'Potwierdzenie',
  needsApproval: 'Wymagana jest Twoja zgoda',
  running: 'Uruchamianie',
  completed: 'Ukończono',
  rejectedStatus: 'Odrzucono',
  failedStatus: 'Nie powiodło się',
  statusUnavailable: 'Stan niedostępny',
  actionLabel: 'Działanie',
  connectionLabel: 'Połączenie',
  argumentsLabel: 'Argumenty',
  tapOnly: 'Działania w połączonych usługach zatwierdza się dotknięciem.',
  approveAndRun: 'Zatwierdź i uruchom',
  working: 'Pracuję…',
  connectedServiceAction: 'Działanie połączonej usługi',
  connectedService: 'Połączona usługa',
};

const ro = {
  approve: 'Aprobă',
  reject: 'Respinge',
  approvingIn: 'Aprobă · {seconds} s',
  rejectingIn: 'Respinge · {seconds} s',
  cancel: 'Anulează',
  holdToSayYes: 'Ține apăsat pentru da sau nu',
  heardYou: 'Am auzit: „{text}”',
  notSure: 'Nu a fost un da sau nu clar. Atinge pentru a răspunde.',
  confirmForget: 'Uiți această memorie?',
  forgotten: 'Uitare',
  stagedExpired: 'Cererea a expirat. Cere din nou dacă încă o dorești.',
  approvalNeedsAdmin: 'Un administrator trebuie să aprobe acest lucru.',
  tapToApproveConnector: 'Acțiunile serviciilor conectate se aprobă prin atingere.',
  executed: 'Gata',
  failed: 'Nu a reușit.',
  unknownCheckService: 'Rezultatul nu a putut fi confirmat. Verifică serviciul.',
  withdrawn: 'Retras',
  errorStale: 'Cardul s-a schimbat. Verifică-l din nou.',
  errorSpokenApprovalsOff: 'Confirmările vocale sunt oprite. Atinge pentru confirmare.',
  errorApproverPolicyMissing: 'Confirmă pe ecran acțiunile serviciilor conectate.',
  errorNotPending: 'Acest lucru a fost deja decis.',
  errorPaymentRequired:
    'Creditul de utilizare al companiei s-a epuizat. Un administrator poate adăuga un card.',
  reviewBeforeRuns: 'Verifică înainte de rulare',
  confirmation: 'Confirmare',
  needsApproval: 'Este necesară aprobarea ta',
  running: 'Se execută',
  completed: 'Finalizat',
  rejectedStatus: 'Respins',
  failedStatus: 'Eșuat',
  statusUnavailable: 'Stare indisponibilă',
  actionLabel: 'Acțiune',
  connectionLabel: 'Conexiune',
  argumentsLabel: 'Argumente',
  tapOnly: 'Acțiunile serviciilor conectate se aprobă prin atingere.',
  approveAndRun: 'Aprobă și rulează',
  working: 'Se lucrează…',
  connectedServiceAction: 'Acțiune a serviciului conectat',
  connectedService: 'Serviciu conectat',
};

const sk = {
  approve: 'Schváliť',
  reject: 'Zamietnuť',
  approvingIn: 'Schváliť · {seconds} s',
  rejectingIn: 'Zamietnuť · {seconds} s',
  cancel: 'Zrušiť',
  holdToSayYes: 'Podržte pre odpoveď áno alebo nie',
  heardYou: 'Počul som: „{text}“',
  notSure: 'Nebolo to jasné áno ani nie. Ťuknutím odpovedzte.',
  confirmForget: 'Zabudnúť túto pamäť?',
  forgotten: 'Zabudnuté',
  stagedExpired: 'Táto žiadosť vypršala. Ak ju stále chcete, požiadajte znova.',
  approvalNeedsAdmin: 'Toto musí schváliť správca.',
  tapToApproveConnector: 'Akcie v pripojených službách sa schvaľujú ťuknutím.',
  executed: 'Hotovo',
  failed: 'Toto neprešlo.',
  unknownCheckService: 'Výsledok sa nedal potvrdiť. Skontrolujte službu.',
  withdrawn: 'Stiahnuté',
  errorStale: 'Karta sa zmenila. Skontrolujte ju znova.',
  errorSpokenApprovalsOff: 'Hlasové potvrdenia sú vypnuté. Ťuknutím potvrďte.',
  errorApproverPolicyMissing: 'Potvrďte akcie pripojených služieb na obrazovke.',
  errorNotPending: 'O tomto už bolo rozhodnuté.',
  errorPaymentRequired: 'Kredit spoločnosti sa minul. Správca môže pridať kartu.',
  reviewBeforeRuns: 'Skontrolovať pred spustením',
  confirmation: 'Potvrdenie',
  needsApproval: 'Vyžaduje sa vaše schválenie',
  running: 'Spúšťa sa',
  completed: 'Dokončené',
  rejectedStatus: 'Zamietnuté',
  failedStatus: 'Zlyhalo',
  statusUnavailable: 'Stav nie je dostupný',
  actionLabel: 'Akcia',
  connectionLabel: 'Pripojenie',
  argumentsLabel: 'Argumenty',
  tapOnly: 'Akcie v pripojených službách sa schvaľujú ťuknutím.',
  approveAndRun: 'Schváliť a spustiť',
  working: 'Pracuje sa…',
  connectedServiceAction: 'Akcia pripojenej služby',
  connectedService: 'Pripojená služba',
};

const sl = {
  approve: 'Odobri',
  reject: 'Zavrni',
  approvingIn: 'Odobri · {seconds} s',
  rejectingIn: 'Zavrni · {seconds} s',
  cancel: 'Prekliči',
  holdToSayYes: 'Pridrži za odgovor da ali ne',
  heardYou: 'Slišal sem: »{text}«',
  notSure: 'To ni bil jasen da ali ne. Dotakni se za odgovor.',
  confirmForget: 'Pozabim ta spomin?',
  forgotten: 'Pozabljeno',
  stagedExpired: 'Ta zahteva je potekla. Zahtevaj jo znova, če jo še želiš.',
  approvalNeedsAdmin: 'To mora odobriti skrbnik.',
  tapToApproveConnector: 'Dejanja v povezanih storitvah se odobrijo z dotikom.',
  executed: 'Končano',
  failed: 'To ni uspelo.',
  unknownCheckService: 'Rezultata ni bilo mogoče potrditi. Preveri storitev.',
  withdrawn: 'Umik',
  errorStale: 'Kartica se je spremenila. Ponovno jo preveri.',
  errorSpokenApprovalsOff: 'Glasovne potrditve so izklopljene. Dotakni se za potrditev.',
  errorApproverPolicyMissing: 'Dejanja povezanih storitev potrdi na zaslonu.',
  errorNotPending: 'O tem je bilo že odločeno.',
  errorPaymentRequired: 'Kredit podjetja je porabljen. Skrbnik lahko doda kartico.',
  reviewBeforeRuns: 'Preveri pred zagonom',
  confirmation: 'Potrditev',
  needsApproval: 'Potrebna je tvoja odobritev',
  running: 'V izvajanju',
  completed: 'Dokončano',
  rejectedStatus: 'Zavrnjeno',
  failedStatus: 'Neuspešno',
  statusUnavailable: 'Stanje ni na voljo',
  actionLabel: 'Dejanje',
  connectionLabel: 'Povezava',
  argumentsLabel: 'Argumenti',
  tapOnly: 'Dejanja v povezanih storitvah se odobrijo z dotikom.',
  approveAndRun: 'Odobri in zaženi',
  working: 'V delu…',
  connectedServiceAction: 'Dejanje povezane storitve',
  connectedService: 'Povezana storitev',
};

const lv = {
  approve: 'Apstiprināt',
  reject: 'Noraidīt',
  approvingIn: 'Apstiprināt · {seconds} s',
  rejectingIn: 'Noraidīt · {seconds} s',
  cancel: 'Atcelt',
  holdToSayYes: 'Turiet, lai atbildētu jā vai nē',
  heardYou: 'Dzirdēju: “{text}”',
  notSure: 'Tā nebija skaidra jā vai nē atbilde. Pieskarieties, lai atbildētu.',
  confirmForget: 'Aizmirst šo atmiņu?',
  forgotten: 'Aizmirsts',
  stagedExpired: 'Šis pieprasījums ir beidzies. Pieprasiet vēlreiz, ja joprojām to vēlaties.',
  approvalNeedsAdmin: 'Administratoram tas jāapstiprina.',
  tapToApproveConnector: 'Darbības savienotajos pakalpojumos apstiprina ar pieskārienu.',
  executed: 'Gatavs',
  failed: 'Darbība neizdevās.',
  unknownCheckService: 'Rezultātu nevarēja apstiprināt. Pārbaudiet pakalpojumu.',
  withdrawn: 'Atsaukts',
  errorStale: 'Šī kartīte mainījās. Pārbaudiet vēlreiz.',
  errorSpokenApprovalsOff: 'Balss apstiprinājumi ir izslēgti. Pieskarieties, lai apstiprinātu.',
  errorApproverPolicyMissing: 'Apstipriniet savienoto pakalpojumu darbības ekrānā.',
  errorNotPending: 'Tas jau ir izlemts.',
  errorPaymentRequired:
    'Uzņēmuma lietošanas kredīts ir iztērēts. Administrators var pievienot karti.',
  reviewBeforeRuns: 'Pārskatīt pirms palaišanas',
  confirmation: 'Apstiprinājums',
  needsApproval: 'Nepieciešams jūsu apstiprinājums',
  running: 'Darbojas',
  completed: 'Pabeigts',
  rejectedStatus: 'Noraidīts',
  failedStatus: 'Neizdevās',
  statusUnavailable: 'Statuss nav pieejams',
  actionLabel: 'Darbība',
  connectionLabel: 'Savienojums',
  argumentsLabel: 'Argumenti',
  tapOnly: 'Darbības savienotajos pakalpojumos apstiprina ar pieskārienu.',
  approveAndRun: 'Apstiprināt un palaist',
  working: 'Notiek darbs…',
  connectedServiceAction: 'Savienota pakalpojuma darbība',
  connectedService: 'Savienots pakalpojums',
};

const lt = {
  approve: 'Patvirtinti',
  reject: 'Atmesti',
  approvingIn: 'Patvirtinti · {seconds} s',
  rejectingIn: 'Atmesti · {seconds} s',
  cancel: 'Atšaukti',
  holdToSayYes: 'Laikykite, kad atsakytumėte taip arba ne',
  heardYou: 'Išgirdau: „{text}“',
  notSure: 'Tai nebuvo aiškus taip arba ne. Palieskite, kad atsakytumėte.',
  confirmForget: 'Pamiršti šią atmintį?',
  forgotten: 'Pamiršta',
  stagedExpired: 'Šis prašymas nebegalioja. Prašykite dar kartą, jei jo vis dar norite.',
  approvalNeedsAdmin: 'Tai turi patvirtinti administratorius.',
  tapToApproveConnector: 'Prijungtų paslaugų veiksmai patvirtinami palietus.',
  executed: 'Atlikta',
  failed: 'Nepavyko.',
  unknownCheckService: 'Rezultato nepavyko patvirtinti. Patikrinkite paslaugą.',
  withdrawn: 'Atšaukta',
  errorStale: 'Kortelė pasikeitė. Patikrinkite ją dar kartą.',
  errorSpokenApprovalsOff: 'Balso patvirtinimai išjungti. Palieskite, kad patvirtintumėte.',
  errorApproverPolicyMissing: 'Patvirtinkite prijungtų paslaugų veiksmus ekrane.',
  errorNotPending: 'Tai jau nuspręsta.',
  errorPaymentRequired:
    'Įmonės naudojimo kreditas išnaudotas. Administratorius gali pridėti kortelę.',
  reviewBeforeRuns: 'Peržiūrėti prieš paleidžiant',
  confirmation: 'Patvirtinimas',
  needsApproval: 'Reikia jūsų patvirtinimo',
  running: 'Vykdoma',
  completed: 'Užbaigta',
  rejectedStatus: 'Atmesta',
  failedStatus: 'Nepavyko',
  statusUnavailable: 'Būsena nepasiekiama',
  actionLabel: 'Veiksmas',
  connectionLabel: 'Ryšys',
  argumentsLabel: 'Argumentai',
  tapOnly: 'Prijungtų paslaugų veiksmai patvirtinami palietus.',
  approveAndRun: 'Patvirtinti ir paleisti',
  working: 'Vykdoma…',
  connectedServiceAction: 'Prijungtos paslaugos veiksmas',
  connectedService: 'Prijungta paslauga',
};

const ga = {
  approve: 'Ceadaigh',
  reject: 'Diúltaigh',
  approvingIn: 'Ceadaigh · {seconds} s',
  rejectingIn: 'Diúltaigh · {seconds} s',
  cancel: 'Cealaigh',
  holdToSayYes: 'Coinnigh chun tá nó níl a fhreagairt',
  heardYou: 'Chuala: “{text}”',
  notSure: 'Ní raibh tá ná níl soiléir ann. Tapáil chun freagra a thabhairt.',
  confirmForget: 'Dearmad a dhéanamh ar an gcuimhne seo?',
  forgotten: 'Dearmadta',
  stagedExpired: 'Chuaigh an t-iarratas seo in éag. Iarr arís é más mian leat é fós.',
  approvalNeedsAdmin: 'Caithfidh riarthóir é seo a cheadú.',
  tapToApproveConnector: 'Ceadaítear gníomhartha i seirbhísí nasctha le sconna.',
  executed: 'Déanta',
  failed: 'Níor éirigh leis seo.',
  unknownCheckService: 'Níorbh fhéidir an toradh a dheimhniú. Seiceáil an tseirbhís.',
  withdrawn: 'Tarraingthe siar',
  errorStale: 'Athraíodh an cárta seo. Déan athbhreithniú air arís.',
  errorSpokenApprovalsOff: 'Tá deimhniúcháin labhartha múchta. Tapáil chun deimhniú.',
  errorApproverPolicyMissing: 'Deimhnigh gníomhartha seirbhísí nasctha ar an scáileán.',
  errorNotPending: 'Socraíodh é seo cheana.',
  errorPaymentRequired:
    'Tá creidmheas úsáide na cuideachta ídithe. Is féidir le riarthóir cárta a chur leis.',
  reviewBeforeRuns: 'Déan athbhreithniú roimh rith',
  confirmation: 'Deimhniú',
  needsApproval: 'Tá do cheadú ag teastáil',
  running: 'Ag rith',
  completed: 'Críochnaithe',
  rejectedStatus: 'Diúltaithe',
  failedStatus: 'Theip',
  statusUnavailable: 'Stádas gan fáil',
  actionLabel: 'Gníomh',
  connectionLabel: 'Ceangal',
  argumentsLabel: 'Argóintí',
  tapOnly: 'Ceadaítear gníomhartha i seirbhísí nasctha le sconna.',
  approveAndRun: 'Ceadaigh agus rith',
  working: 'Ag obair…',
  connectedServiceAction: 'Gníomh seirbhíse nasctha',
  connectedService: 'Seirbhís nasctha',
};

const mt = {
  approve: 'Approva',
  reject: 'Irrifjuta',
  approvingIn: 'Approva · {seconds} s',
  rejectingIn: 'Irrifjuta · {seconds} s',
  cancel: 'Ikkanċella',
  holdToSayYes: 'Żomm biex twieġeb iva jew le',
  heardYou: 'Smajt: “{text}”',
  notSure: 'Ma kienx iva jew le ċar. Tektek biex twieġeb.',
  confirmForget: 'Tinsa din il-memorja?',
  forgotten: 'Minsija',
  stagedExpired: 'Din it-talba skadiet. Itlobha mill-ġdid jekk għad tridha.',
  approvalNeedsAdmin: 'Amministratur irid japprova dan.',
  tapToApproveConnector: 'Azzjonijiet f’servizzi konnessi jiġu approvati b’taptap.',
  executed: 'Lest',
  failed: 'Dan ma għaddiex.',
  unknownCheckService: 'Ir-riżultat ma setax jiġi kkonfermat. Iċċekkja s-servizz.',
  withdrawn: 'Irtirat',
  errorStale: 'Din il-karta nbidlet. Erġa’ rrevediha.',
  errorSpokenApprovalsOff: 'Konfermi mitkellma huma mitfija. Tektek biex tikkonferma.',
  errorApproverPolicyMissing: 'Ikkonferma azzjonijiet ta’ servizzi konnessi fuq l-iskrin.',
  errorNotPending: 'Dan diġà ġie deċiż.',
  errorPaymentRequired: 'Il-kreditu tal-użu tal-kumpanija spiċċa. Amministratur jista’ jżid karta.',
  reviewBeforeRuns: 'Irrevedi qabel ma tmexxi',
  confirmation: 'Konferma',
  needsApproval: 'Jeħtieġ l-approvazzjoni tiegħek',
  running: 'Qed jaħdem',
  completed: 'Lest',
  rejectedStatus: 'Miċħud',
  failedStatus: 'Falla',
  statusUnavailable: 'L-istatus mhux disponibbli',
  actionLabel: 'Azzjoni',
  connectionLabel: 'Konnessjoni',
  argumentsLabel: 'Argumenti',
  tapOnly: 'Azzjonijiet f’servizzi konnessi jiġu approvati b’taptap.',
  approveAndRun: 'Approva u mexxi',
  working: 'Qed jaħdem…',
  connectedServiceAction: 'Azzjoni ta’ servizz konness',
  connectedService: 'Servizz konness',
};

const hu = {
  approve: 'Jóváhagyás',
  reject: 'Elutasítás',
  approvingIn: 'Jóváhagyás · {seconds} s',
  rejectingIn: 'Elutasítás · {seconds} s',
  cancel: 'Mégse',
  holdToSayYes: 'Tartsd lenyomva az igen vagy nem válaszhoz',
  heardYou: 'Ezt hallottam: „{text}”',
  notSure: 'Ez nem volt egyértelmű igen vagy nem. Koppints a válaszhoz.',
  confirmForget: 'Elfelejted ezt az emléket?',
  forgotten: 'Elfelejtve',
  stagedExpired: 'Ez a kérés lejárt. Kérd újra, ha még mindig szeretnéd.',
  approvalNeedsAdmin: 'Ezt egy rendszergazdának kell jóváhagynia.',
  tapToApproveConnector: 'A csatlakoztatott szolgáltatások műveleteit koppintással hagyod jóvá.',
  executed: 'Kész',
  failed: 'Ez nem ment végig.',
  unknownCheckService: 'Az eredmény nem erősíthető meg. Ellenőrizd a szolgáltatást.',
  withdrawn: 'Visszavonva',
  errorStale: 'A kártya megváltozott. Ellenőrizd újra.',
  errorSpokenApprovalsOff:
    'A beszéddel való jóváhagyás ki van kapcsolva. Koppints a megerősítéshez.',
  errorApproverPolicyMissing:
    'A képernyőn erősítsd meg a csatlakoztatott szolgáltatások műveleteit.',
  errorNotPending: 'Erről már döntöttek.',
  errorPaymentRequired:
    'A vállalat használati kerete elfogyott. A rendszergazda hozzáadhat egy kártyát.',
  reviewBeforeRuns: 'Ellenőrzés futtatás előtt',
  confirmation: 'Megerősítés',
  needsApproval: 'Jóváhagyásod szükséges',
  running: 'Fut',
  completed: 'Befejezve',
  rejectedStatus: 'Elutasítva',
  failedStatus: 'Sikertelen',
  statusUnavailable: 'Az állapot nem érhető el',
  actionLabel: 'Művelet',
  connectionLabel: 'Kapcsolat',
  argumentsLabel: 'Argumentumok',
  tapOnly: 'A csatlakoztatott szolgáltatások műveleteit koppintással hagyod jóvá.',
  approveAndRun: 'Jóváhagyás és futtatás',
  working: 'Feldolgozás…',
  connectedServiceAction: 'Csatlakoztatott szolgáltatás művelete',
  connectedService: 'Csatlakoztatott szolgáltatás',
};

const el = {
  approve: 'Έγκριση',
  reject: 'Απόρριψη',
  approvingIn: 'Έγκριση · {seconds} s',
  rejectingIn: 'Απόρριψη · {seconds} s',
  cancel: 'Ακύρωση',
  holdToSayYes: 'Κρατήστε για απάντηση ναι ή όχι',
  heardYou: 'Άκουσα: «{text}»',
  notSure: 'Δεν ήταν σαφές ναι ή όχι. Πατήστε για απάντηση.',
  confirmForget: 'Να ξεχαστεί αυτή η μνήμη;',
  forgotten: 'Ξεχάστηκε',
  stagedExpired: 'Το αίτημα έληξε. Ζητήστε το ξανά αν το θέλετε ακόμη.',
  approvalNeedsAdmin: 'Απαιτείται έγκριση διαχειριστή.',
  tapToApproveConnector: 'Οι ενέργειες σε συνδεδεμένες υπηρεσίες εγκρίνονται με πάτημα.',
  executed: 'Ολοκληρώθηκε',
  failed: 'Δεν ολοκληρώθηκε.',
  unknownCheckService: 'Το αποτέλεσμα δεν επιβεβαιώθηκε. Ελέγξτε την υπηρεσία.',
  withdrawn: 'Ανακλήθηκε',
  errorStale: 'Η κάρτα άλλαξε. Ελέγξτε την ξανά.',
  errorSpokenApprovalsOff:
    'Οι φωνητικές επιβεβαιώσεις είναι απενεργοποιημένες. Πατήστε για επιβεβαίωση.',
  errorApproverPolicyMissing: 'Επιβεβαιώστε στην οθόνη τις ενέργειες συνδεδεμένων υπηρεσιών.',
  errorNotPending: 'Έχει ήδη αποφασιστεί.',
  errorPaymentRequired:
    'Η πίστωση χρήσης της εταιρείας εξαντλήθηκε. Ο διαχειριστής μπορεί να προσθέσει κάρτα.',
  reviewBeforeRuns: 'Ελέγξτε πριν την εκτέλεση',
  confirmation: 'Επιβεβαίωση',
  needsApproval: 'Απαιτείται η έγκρισή σας',
  running: 'Εκτελείται',
  completed: 'Ολοκληρώθηκε',
  rejectedStatus: 'Απορρίφθηκε',
  failedStatus: 'Απέτυχε',
  statusUnavailable: 'Η κατάσταση δεν είναι διαθέσιμη',
  actionLabel: 'Ενέργεια',
  connectionLabel: 'Σύνδεση',
  argumentsLabel: 'Ορίσματα',
  tapOnly: 'Οι ενέργειες σε συνδεδεμένες υπηρεσίες εγκρίνονται με πάτημα.',
  approveAndRun: 'Έγκριση και εκτέλεση',
  working: 'Επεξεργασία…',
  connectedServiceAction: 'Ενέργεια συνδεδεμένης υπηρεσίας',
  connectedService: 'Συνδεδεμένη υπηρεσία',
};

const voiceConfirmCopy: NamespaceCatalog<VoiceConfirmCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default voiceConfirmCopy;
